import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { StateService } from '@uirouter/angular';
import * as _ from 'lodash';

import { WikiHelperService } from '../services/wiki.helper';
import { StateHelperService } from '../../../core/services/core.ui.state.service';

/**
 * Configurable wiki View panel — renders wiki content via `<view-wiki>` (macro expansion +
 * internal-link interception) with an optional right column (table-of-contents + References).
 * Shared by the wiki page (sidebar on) and the project/site homepages (sidebar off).
 */
@Component({
    selector: 'wiki-view',
    template: require('./wiki.view.component.html'),
})
export class WikiViewComponent implements OnInit, OnChanges {
    /** Page object; content is read from `rel.content.data` unless `wikiHtml` is supplied. */
    @Input() wikiPage: any = null;
    /** Pre-resolved content (overrides `wikiPage.rel.content.data`). */
    @Input() wikiHtml: string | null = null;
    /** Project, used for the default macro context and reference-link params. */
    @Input() project: any = null;
    /** Project menu (module availability) — used to build the default macro context. */
    @Input() projectMenu: any = null;
    /** Macro menu context (availability of tracker/build, etc.). Defaults built from project/projectMenu. */
    @Input() macroInputs: any = null;
    /** Show the TOC + References right column. */
    @Input() showSidebar: boolean = false;
    /** Unix name used when building reference links (project wiki). */
    @Input() unixName: string = '';
    /** State reference links navigate to. */
    @Input() linkState: string = 'home.project.summary.wiki.page';

    html: string = '';
    links: { internal: any[]; external: any[] } = { internal: [], external: [] };
    private internalUrlBase: string = '';

    constructor(
        private stateService: StateService,
        private wikiHelper: WikiHelperService,
        private stateHelperService: StateHelperService,
    ) {}

    ngOnInit(): void {
        this.render();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.wikiPage || changes.wikiHtml || changes.macroInputs) {
            this.render();
        }
    }

    private render(): void {
        const content = this.wikiHtml != null ? this.wikiHtml : _.get(this.wikiPage, 'rel.content.data', '');

        const urlFn = (pagename: string): string => {
            const params: any = { pagename };
            if (this.unixName) { params.unixName = this.unixName; }
            try {
                return this.stateService.href(this.linkState, params);
            } catch (e) {
                return '';
            }
        };

        this.html = this.wikiHelper.addHeadingIds(this.wikiHelper.convertWikiWords(content, urlFn));
        if (!this.macroInputs) {
            this.macroInputs = {
                project: this.project,
                availableModules: {
                    tracker: this.moduleAvailable('tracker'),
                    build: this.moduleAvailable('build'),
                },
            };
        }

        if (this.showSidebar) {
            this.internalUrlBase = urlFn('');
            this.links = this.wikiHelper.getLinks(this.html, this.internalUrlBase);
            _.forEach(this.links.internal, (link: any) => {
                try {
                    const parsed = new URL(link.url, window.location.origin);
                    link.pagename = parsed.searchParams.get('pagename') || link.title;
                } catch (e) {
                    link.pagename = link.title;
                }
            });
        } else {
            this.links = { internal: [], external: [] };
        }
    }

    /** Route internal links inside rendered wiki HTML through UI-Router. */
    onContentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const anchor = target.closest('a') as HTMLAnchorElement;
        if (!anchor || !this.internalUrlBase) { return; }

        const href = anchor.getAttribute('href');
        if (!href || !href.startsWith(this.internalUrlBase)) { return; }

        event.preventDefault();
        this.stateHelperService.navigateByUrl(href);
    }

    private moduleAvailable(moduleName: string): boolean {
        const modules = _.get(this.projectMenu, 'modules', []);
        return _.includes(modules, _.lowerCase(moduleName)) &&
            (_.get(this.projectMenu, _.lowerCase(moduleName), 0) > 0);
    }

    get hasReferences(): boolean {
        return this.links.internal.length > 0 || this.links.external.length > 0;
    }
}
